import React, { useState, useEffect } from 'react'
import { PageHeader, Button, Table, Skeleton, Popconfirm, Modal } from 'antd'
import { useQuery } from '@apollo/react-hooks'
import { PlusOutlined, DeleteOutlined, CloudUploadOutlined } from '@ant-design/icons'

import ResultError from '@packages/ResultError'
import { PROVIDERS_ICON } from '@constants/IconsConstants'
import { useColumnFilterProps } from '@utils/columnFilter'
import { getProvidersQuery } from '@graphql/queries'
import { useUpdateProvider, useDeleteProvider } from '@graphql/mutations'

import EditableCell from '../../EditableCell'
import ManageProviderForm from '../ManageProviderForm'
import ManageProviderCsvUpload from '../ManageProviderCsvUpload'

const tableProps = {
  size: 'middle',
  pagination: false,
}
const newButtonProps = {
  type: 'primary',
  icon: <PlusOutlined />,
  key: 'newProvider',
}
const csvButtonProps = {
  icon: <CloudUploadOutlined />,
  key: 'uploadCsv',
}

function ManageProviderTable() {
  const [providersData, setProvidersData] = useState(false)
  const [formModalVisible, setFormModalVisible] = useState(false)
  const [csvModalVisible, setCsvModalVisible] = useState(false)
  const { error, data } = useQuery(getProvidersQuery)
  const [updateProvider] = useUpdateProvider()
  const [deleteProvider] = useDeleteProvider()
  const useFilterField = field => useColumnFilterProps(field)
  const toggleFormModal = () => setFormModalVisible(!formModalVisible)
  const toggleCsvModal = () => setCsvModalVisible(!csvModalVisible)

  useEffect(() => {
    if (data) setProvidersData(data.providers)
  }, [data])

  const handleSave = data => {
    const { id, name } = data
    const newData = [...providersData]
    const index = newData.findIndex(item => item.id === data.id)
    const item = newData[index]

    newData.splice(index, 1, { ...item, ...data })

    if (JSON.stringify(newData) !== JSON.stringify(providersData)) {
      updateProvider({
        variables: {
          id,
          data: {
            name: { set: name },
          },
        },
      })

      setProvidersData(newData)
    }
  }

  const handleDelete = id => {
    deleteProvider({
      variables: { id },
      refetchQueries: ['GetProvidersQuery'],
    })
  }
  const columns = [
    {
      dataIndex: 'name',
      title: 'Nome',
      editable: true,
      ...useFilterField('name'),
    },
    {
      align: 'center',
      render: (value, { id, name }) => (
        <Popconfirm
          title={`Tem a certeza que quer eliminar ${name} ?`}
          onConfirm={() => handleDelete(id)}
          okText="Sim"
          cancelText="Não"
        >
          <DeleteOutlined />
        </Popconfirm>
      ),
    },
  ]
  const mappedColumns = columns.map(col => {
    if (!col.editable) return col

    return {
      ...col,
      onCell: record => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    }
  })

  return (
    <>
      <Modal
        title="Novo fornecedor"
        visible={formModalVisible}
        onCancel={() => toggleFormModal()}
        footer={null}
      >
        <ManageProviderForm afterSubmit={() => toggleFormModal()} />
      </Modal>

      <Modal
        title="Carregar ficheiro CSV"
        visible={csvModalVisible}
        onCancel={() => toggleCsvModal()}
        footer={null}
      >
        <ManageProviderCsvUpload afterSubmit={() => toggleCsvModal()} />
      </Modal>

      <PageHeader
        title="Fornecedores"
        avatar={{ src: PROVIDERS_ICON }}
        extra={[
          <Button {...newButtonProps} onClick={() => toggleFormModal()}>
            Novo fornecedor
          </Button>,
          <Button {...csvButtonProps} onClick={() => toggleCsvModal()}>
            CSV
          </Button>,
        ]}
      />

      {error && <ResultError />}

      <Skeleton loading={!error && !providersData} active>
        {providersData && (
          <Table
            components={EditableCell.Components}
            columns={mappedColumns}
            dataSource={providersData}
            rowKey={({ id }) => id}
            rowClassName={() => 'editable-row'}
            {...tableProps}
          />
        )}
      </Skeleton>
    </>
  )
}

export default ManageProviderTable
